import React, { useState, useEffect, useCallback } from 'react';
import authStorage from '../utils/authStorage';
import { Link } from 'react-router-dom';
import { Check, AlertCircle, Crown, Zap, Shield, RefreshCw, CheckCircle, XCircle, Clock, LifeBuoy } from 'lucide-react';
import { vendorService } from '../services/api';
import { useToast } from '../hooks/useToast';

// Load Razorpay SDK dynamically
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PLANS = [
  {
    key: 'FREE',
    name: 'Free',
    price: 'Free',
    priceNum: 0,
    period: 'Lifetime',
    icon: Shield,
    color: '#6b7280',
    borderColor: 'border-gray-200',
    badgeColor: 'bg-gray-100 text-gray-600',
    features: [
      'Vendor Profile',
      'Category Listing',
      'Company Info',
      'Direct Inquiry Form',
    ],
    locked: [
      'Direct Call / WhatsApp',
      'Product / Service Photos',
      'Priority Placement',
    ],
  },
  {
    key: 'PROFESSIONAL',
    name: 'Professional',
    price: '₹12,000',
    priceNum: 12000,
    originalPrice: '₹20,000',
    discountPercent: '40%',
    period: '/year',
    popular: true,
    icon: Zap,
    color: '#2ab391',
    borderColor: 'border-[#2ab391]',
    badgeColor: 'bg-[#eaf7f4] text-[#2ab391]',
    features: [
      'Vendor Profile',
      'Category Listing',
      'Company Info',
      'Direct Inquiry Form',
      'Direct Call / WhatsApp',
      'Product / Service Photos',
      'Priority Placement',
    ],
    locked: [],
  },
  {
    key: 'PREMIUM',
    name: 'Premium',
    price: '₹25,000',
    priceNum: 25000,
    originalPrice: '₹40,000',
    discountPercent: '37.5%',
    period: '/year',
    icon: Crown,
    color: '#7c3aed',
    borderColor: 'border-violet-400',
    badgeColor: 'bg-violet-100 text-violet-700',
    features: [
      'Vendor Profile',
      'Category Listing',
      'Company Info',
      'Direct Inquiry Form',
      'Direct Call / WhatsApp',
      'Product / Service Photos',
      'Placement on Top (Highest Priority)',
      'Trusted Vendor Badge',
      'Social Media Feature (1 post/reel)',
      'Website Feature (1 Blog post)',
    ],
    locked: [],
  },
];

const StatusBadge = ({ status }) => {
  if (status === 'ACTIVE') return (
    <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">
      <CheckCircle size={11} /> Active
    </span>
  );
  if (status === 'EXPIRED') return (
    <span className="inline-flex items-center gap-1 bg-red-100 text-red-600 text-xs font-bold px-2.5 py-1 rounded-full">
      <XCircle size={11} /> Expired
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-xs font-bold px-2.5 py-1 rounded-full">
      <Clock size={11} /> {status}
    </span>
  );
};

const UpgradeMembership = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('upgrade');
  const [membership, setMembership] = useState(null);
  const [loadingMembership, setLoadingMembership] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null); // Which plan is being paid
  const [isIndividualListing, setIsIndividualListing] = useState(false);

  const fetchMembership = useCallback(async () => {
    setLoadingMembership(true);
    try {
      const listingRes = await vendorService.getMyListings();
      if (listingRes.success && listingRes.data) {
        const listings = listingRes.data.listings || (Array.isArray(listingRes.data) ? listingRes.data : []);
        if (listings.length > 0) {
          const first = listings[0];
          const type = (first.listingType || first.listing_type || '').toUpperCase();
          if (type === 'INDIVIDUAL') {
            setIsIndividualListing(true);
            setLoadingMembership(false);
            return;
          }
        }
      }
    } catch (err) {
      console.error(err);
    }

    const res = await vendorService.getCurrentMembership();
    if (res.success) {
      setMembership(res.data);
    } else {
      // If API fails, default to free
      setMembership({ plan_name: 'FREE', status: 'ACTIVE', is_lifetime: true });
    }
    setLoadingMembership(false);
  }, []);

  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  const handleUpgrade = async (plan) => {
    if (plan.key === 'FREE') return;

    const sdkLoaded = await loadRazorpay();
    if (!sdkLoaded) {
      toast.error('Failed to load payment gateway. Please check your connection.');
      return;
    }

    setProcessingPlan(plan.key);
    const orderRes = await vendorService.createMembershipOrder(plan.key);
    if (!orderRes.success) {
      toast.error(orderRes.error || 'Failed to create payment order. Please try again.');
      setProcessingPlan(null);
      return;
    }

    const { order_id, amount, currency, key_id } = orderRes.data;
    const user = JSON.parse(authStorage.getItem('user') || '{}');

    const options = {
      key: key_id,
      amount,
      currency,
      name: 'Top Interiors',
      description: `${plan.name} Membership – Yearly`,
      order_id,
      prefill: {
        name: user.name || '',
        email: user.emailId || '',
      },
      theme: { color: plan.color },
      handler: async (response) => {
        // Payment succeeded on Razorpay side — now verify server-side
        const verifyRes = await vendorService.verifyMembershipPayment({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          plan_name: plan.key,
        });

        if (verifyRes.success) {
          toast.success(`🎉 ${plan.name} plan activated! Your membership is valid for 1 year.`);
          await fetchMembership();   // Refresh current plan
          setActiveTab('current');   // Switch to Current tab
        } else {
          toast.error(verifyRes.error || 'Payment verification failed. Please contact support.');
        }
        setProcessingPlan(null);
      },
      modal: {
        ondismiss: () => {
          setProcessingPlan(null);
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      toast.error(`Payment failed: ${resp.error.description || 'Unknown error'}`);
      setProcessingPlan(null);
    });
    rzp.open();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  const currentPlanKey = membership?.plan_name || 'FREE';
  const isExpired = membership?.is_expired || membership?.status === 'EXPIRED';

  const getPlanInfo = (key) => PLANS.find(p => p.key === key) || PLANS[0];
  const currentPlanInfo = getPlanInfo(currentPlanKey);

  if (isIndividualListing) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-left">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-xs text-center space-y-4">
          <div className="w-16 h-16 bg-[#eaf7f4] text-[#2ab391] rounded-full flex items-center justify-center mx-auto">
            <CheckCircle size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription Not Required</h2>
          <p className="text-gray-600 text-sm max-w-lg mx-auto leading-relaxed">
            Upgrade Membership is not required for Individual Business listings. All profile features, direct call options, and WhatsApp communication features are fully unlocked for your listing.
          </p>
          <div className="pt-2">
            <Link
              to="/my-account/manage-listing"
              className="inline-flex items-center gap-2 bg-[#2ab391] hover:bg-[#23977a] text-white font-semibold px-6 py-2.5 rounded-xl transition-all shadow-xs"
            >
              Manage Your Listing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h2 className="text-xl font-bold text-gray-800">Membership</h2>
        <div className="flex gap-6 text-sm font-medium">
          <button
            className={`${activeTab === 'current' ? 'text-[#2ab391] border-b-2 border-[#2ab391] pb-4 -mb-[18px]' : 'text-gray-500 hover:text-gray-800'}`}
            onClick={() => setActiveTab('current')}
          >
            Current
          </button>
          <button
            className={`${activeTab === 'upgrade' ? 'text-[#2ab391] border-b-2 border-[#2ab391] pb-4 -mb-[18px]' : 'text-gray-500 hover:text-gray-800'}`}
            onClick={() => setActiveTab('upgrade')}
          >
            Upgrade
          </button>
        </div>
      </div>

      {/* ── CURRENT TAB ─────────────────────────────────────────── */}
      {activeTab === 'current' && (
        <div className="space-y-4">
          {/* Expiry Warning Banner */}
          {isExpired && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3 items-start">
              <XCircle size={20} className="text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-700 font-semibold text-sm">Your membership plan has expired.</p>
                <p className="text-red-600 text-xs mt-0.5">
                  All premium listing benefits (Direct Call, WhatsApp, Photo Gallery, Priority Placement) are now disabled.
                  Please upgrade below to re-activate your plan.
                </p>
                <button
                  onClick={() => setActiveTab('upgrade')}
                  className="mt-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-4 py-1.5 rounded-lg transition-colors"
                >
                  Renew Membership
                </button>
              </div>
            </div>
          )}

          {loadingMembership ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2ab391]" />
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 shadow-inner">
              {/* Plan badge */}
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: currentPlanInfo.color + '20' }}
                >
                  <currentPlanInfo.icon size={20} style={{ color: currentPlanInfo.color }} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Current Plan</p>
                  <p className="text-lg font-extrabold text-gray-800 leading-tight">{currentPlanInfo.name}</p>
                </div>
                <div className="ml-auto">
                  <StatusBadge status={isExpired ? 'EXPIRED' : 'ACTIVE'} />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden text-sm">
                <div className="flex justify-between p-4 border-b">
                  <span className="text-gray-600 font-medium">Plan Name</span>
                  <span className="font-semibold text-gray-800">
                    {membership?.original_plan_name
                      ? getPlanInfo(membership.original_plan_name).name
                      : currentPlanInfo.name}
                    {isExpired && <span className="ml-2 text-xs text-red-500">(Expired → Downgraded to Free)</span>}
                  </span>
                </div>
                <div className="flex justify-between p-4 border-b">
                  <span className="text-gray-600 font-medium">Package Amount</span>
                  <span className="font-semibold text-gray-800">
                    {membership?.amount ? `₹${membership.amount.toLocaleString('en-IN')}` : 'Free'}
                  </span>
                </div>
                <div className="flex justify-between p-4 border-b">
                  <span className="text-gray-600 font-medium">Duration</span>
                  <span className="font-semibold text-gray-800">
                    {membership?.is_lifetime ? 'Lifetime' : 'Yearly (365 days)'}
                  </span>
                </div>
                <div className="flex justify-between p-4 border-b">
                  <span className="text-gray-600 font-medium">Start Date</span>
                  <span className="font-semibold text-gray-800">
                    {membership?.start_date ? formatDate(membership.start_date) : '—'}
                  </span>
                </div>
                <div className={`flex justify-between p-4 ${isExpired ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <span className="text-gray-600 font-medium">Expire On</span>
                  <span className={`font-bold ${isExpired ? 'text-red-500' : 'text-gray-800'}`}>
                    {membership?.is_lifetime
                      ? 'Lifetime FREE'
                      : membership?.end_date
                        ? formatDate(membership.end_date)
                        : '—'}
                    {isExpired && ' ⚠ Expired'}
                  </span>
                </div>
              </div>

              {/* Active Features Card */}
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-center justify-between border-b pb-3 mb-4">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#2ab391]" />
                    Active Features & Benefits
                  </h3>
                  <span className="text-[11px] font-semibold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full uppercase tracking-wider">
                    {currentPlanInfo.name} Tier
                  </span>
                </div>
                
                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentPlanInfo.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-emerald-50/40 border border-emerald-50 transition-all hover:bg-emerald-50/60">
                      <div className="mt-0.5 bg-emerald-100 text-[#2ab391] rounded-full p-0.5 flex-shrink-0">
                        <Check size={12} />
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{f}</span>
                    </div>
                  ))}
                  
                  {/* Locked Features */}
                  {(isExpired && currentPlanInfo.locked?.length > 0
                    ? currentPlanInfo.locked
                    : getPlanInfo(membership?.original_plan_name || 'FREE').locked || []
                  ).map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-100/50 transition-all opacity-75">
                      <div className="mt-0.5 bg-red-50 text-red-400 rounded-full p-0.5 flex-shrink-0">
                        <XCircle size={12} />
                      </div>
                      <span className="text-xs font-medium text-gray-400 line-through">{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Refresh button */}
              <button
                onClick={fetchMembership}
                className="mt-4 flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#2ab391] transition-colors"
              >
                <RefreshCw size={12} />
                Refresh membership status
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── UPGRADE TAB ─────────────────────────────────────────── */}
      {activeTab === 'upgrade' && (
        <div className="space-y-6">
          {/* Expiry Warning Banner in upgrade tab too */}
          {isExpired && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 items-start">
              <AlertCircle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-amber-700 text-sm">
                <strong>Your previous plan expired.</strong> Select a plan below to renew and restore all benefits.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map((plan) => {
              const isCurrent = plan.key === currentPlanKey && !isExpired;
              const isProcessing = processingPlan === plan.key;
              const PlanIcon = plan.icon;

              return (
                <div
                  key={plan.key}
                  className={`relative rounded-xl bg-white border flex flex-col h-full transition-shadow ${
                    isCurrent ? `${plan.borderColor} shadow-lg border-2 ring-2 ring-[#2ab391]/20` : 'border-gray-200'
                  }`}
                >
                  {isCurrent ? (
                    <div className="absolute top-0 right-0 bg-[#2ab391] text-white text-[10px] font-bold px-3 py-1 uppercase rounded-tr-lg rounded-bl-lg tracking-wider flex items-center gap-1 select-none">
                      <Check size={10} /> Selected Plan
                    </div>
                  ) : (
                    <>
                      {plan.popular && (
                        <div className="absolute top-0 right-0 bg-[#2ab391] text-white text-[10px] font-bold px-3 py-1 uppercase rounded-tr-lg rounded-bl-lg tracking-wider">
                          Most Popular
                        </div>
                      )}
                      {plan.key === 'PREMIUM' && (
                        <div className="absolute top-0 right-0 bg-violet-600 text-white text-[10px] font-bold px-3 py-1 uppercase rounded-tr-lg rounded-bl-lg tracking-wider">
                          Best Value
                        </div>
                      )}
                    </>
                  )}

                  <div className="p-6 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background: plan.color + '18' }}
                      >
                        <PlanIcon size={16} style={{ color: plan.color }} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">{plan.name}</h3>
                    </div>
                    <div className="min-h-[58px] flex flex-col justify-end">
                      {plan.originalPrice && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm line-through opacity-60 font-semibold" style={{ color: plan.color }}>
                            {plan.originalPrice}
                          </span>
                          <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded" style={{ backgroundColor: plan.color + '18', color: plan.color }}>
                            {plan.discountPercent} OFF
                          </span>
                        </div>
                      )}
                      <div className="flex items-end gap-1">
                        <span className="text-3xl font-extrabold leading-none" style={{ color: plan.color }}>
                          {plan.price}
                        </span>
                        <span className="text-gray-500 font-medium text-sm leading-none">{plan.period}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 flex-grow">
                    <ul className="space-y-2.5">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <Check size={15} className="text-[#2ab391] mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                      {(plan.locked || []).map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                          <XCircle size={15} className="text-gray-300 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 pt-0 mt-auto">
                    {isCurrent ? (
                      <button disabled className="w-full py-2.5 rounded text-sm font-bold bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed">
                        Current Plan
                      </button>
                    ) : plan.key === 'FREE' ? (
                      <button disabled className="w-full py-2.5 rounded text-sm font-bold bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed">
                        Default Plan
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUpgrade(plan)}
                        disabled={!!processingPlan}
                        className={`w-full py-2.5 rounded text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                          processingPlan
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : plan.key === 'PREMIUM'
                              ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-md'
                              : 'bg-[#2ab391] hover:bg-[#23977a] text-white'
                        }`}
                      >
                        {isProcessing ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            Processing...
                          </>
                        ) : isExpired && plan.key === (membership?.original_plan_name) ? (
                          'Renew Plan'
                        ) : (
                          `Upgrade to ${plan.name}`
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Help & Support Desk Link */}
          <div className="pt-2 text-center sm:text-left">
            <p className="text-xs sm:text-sm text-gray-500 font-normal">
              Need help with payments or plans?{' '}
              <Link
                to="/my-account/support-tickets"
                className="text-[#2ab391] hover:underline font-semibold cursor-pointer"
              >
                Go to Support Desk
              </Link>
            </p>
          </div>

          <div className="bg-[#f0f9ff] border border-blue-100 text-blue-800 px-4 py-3 rounded text-sm flex gap-2 items-start">
            <AlertCircle size={18} className="text-[#2ab391] mt-0.5 flex-shrink-0" />
            <p>
              Payments are processed securely via Razorpay. All paid plans are valid for <strong>365 days</strong> from the date of activation.
              After expiry, your listing will automatically revert to the Free plan restrictions until renewed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeMembership;
